/* eslint-disable react/jsx-props-no-spreading */
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import SeoData from 'src/common/components/content/Seo/SeoData';
import {
  DetailedSpecificationType,
  ModelDetailType,
} from 'src/modules/newCars/types';
import { trackModelPageLoaded } from 'src/modules/newCars/utils/events/model';
import {
  processModelSeoData,
  processSeoModelDetailData,
} from 'src/modules/newCars/utils/seoContent';
import TabBar from '../../TabBar';
import { Images } from './Images/Images';
import { Mileage } from './Mileage/Mileage';
// import fetchPriceBreakdown from 'src/modules/newCars/services/fetchPriceBreakdown';
import { getRandomNum } from 'core-lib/utils/helpers';
import BreadCrumb from 'src/components/common/bread_crumbs/bread_crumbs';
import { seoData } from 'src/modules/newCars/constants/seoData';
import { PriceBreakuptype } from 'src/modules/newCars/types/model';
import s from './Model.module.scss';

const Faq = dynamic(() => import('../../content/Faq'), {
  ssr: true,
});
const Review = dynamic(() => import('./Review'), {
  ssr: true,
});
const Overview = dynamic(() => import('./Overview'), {
  ssr: true,
});
const Price = dynamic(() => import('./Price'), {
  ssr: true,
});
const Colour = dynamic(() => import('./Colour'), {
  ssr: true,
});
const SpecificationPage = dynamic(() => import('./SpecificationPage'), {
  ssr: true,
});
const VariantTabPage = dynamic(() => import('./VariantTabPage'), {
  ssr: true,
});

type ModelPageType = {
  content: ModelDetailType;
  strapiContent: any;
  isPwa?: boolean;
  slug: string[] | string;
  priceDetail: PriceBreakuptype;
  cookies?: any;
  variantSpecsInModel?: Array<DetailedSpecificationType>;
};

export const Model = ({
  content,
  strapiContent,
  slug,
  priceDetail,
  cookies,
  variantSpecsInModel,
}: ModelPageType) => {
  useEffect(() => {
    trackModelPageLoaded();
  }, []);

  const breadCrumb = [
    {
      id: getRandomNum(),
      name: 'Home',
      type: 'internal',
      url: '/',
    },
    {
      id: getRandomNum(),
      name: 'New Car',
      type: 'internal',
      url: '/new-cars',
    },
    ...(slug[1]
      ? [
          {
            id: getRandomNum(),
            name: `${slug[0].split('-')[0]}  ${slug[1].split('-').join(' ')}`,
            type: 'internal',
            url: `/new-cars/${slug[0]}/${slug[1]}`,
          },
        ]
      : []),
    ...(slug[2]
      ? [
          {
            id: getRandomNum(),
            name: slug[2]?.split('-').join(' '),
            type: 'internal',
            url: `/new-cars/${slug[0]}/${slug[1]}/${slug[2]}`,
          },
        ]
      : []),
  ];
  return (
    <div className={s.root}>
      {slug?.length === 2 ? (
        <SeoData
          data={processSeoModelDetailData(strapiContent?.carListing, content)}
        />
      ) : (
        {
          review: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.review,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          price: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.price,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          images: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.images,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          mileage: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.mileage,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          colour: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.colour,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          variants: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.variants,
                slug[2],
                cookies.cityName
              )}
            />
          ),
          specification: (
            <SeoData
              data={processSeoModelDetailData(
                strapiContent?.carListing,
                content,
                seoData?.specification,
                slug[2],
                cookies.cityName
              )}
            />
          ),
        }[slug[2]]
      )}
      {slug[2]?.includes('price-in') && (
        <SeoData
          data={processSeoModelDetailData(
            strapiContent?.carListing,
            content,
            seoData?.price,
            slug[2],
            cookies.cityName
          )}
        />
      )}
      <TabBar data={content} slug={slug} cityName={cookies?.cityName} />
      <BreadCrumb data={breadCrumb} />
      {slug.length === 2 ? (
        <Overview content={content} strapiContent={strapiContent} />
      ) : (
        {
          review: <Review content={content} strapiContent={strapiContent} />,
          price: (
            <Price
              content={content}
              strapiContent={strapiContent}
              priceBreakup={priceDetail}
              cookies={cookies}
            />
          ),
          images: <Images content={content} strapiContent={strapiContent} />,
          mileage: <Mileage content={content} strapiContent={strapiContent} />,
          colour: <Colour content={content} strapiContent={strapiContent} />,
          variants: (
            <VariantTabPage content={content} strapiContent={strapiContent} />
          ),
          specification: (
            <SpecificationPage
              content={content}
              strapiContent={strapiContent}
              variantSpecsInModel={variantSpecsInModel}
            />
          ),
        }[slug[2]]
      )}
      {slug[2]?.includes('price-in') && (
        <Price
          content={content}
          strapiContent={strapiContent}
          priceBreakup={priceDetail}
          cookies={cookies}
        />
      )}
      <div className={s.infoBox}>
        {strapiContent?.faqTitle && strapiContent?.faqContent && (
          <Faq
            faqTitle={strapiContent?.faqTitle}
            faqContent={processModelSeoData(strapiContent?.faqContent, content)}
          />
        )}
      </div>
    </div>
  );
};
